import React, { useEffect, useState } from 'react'
import { useDisableBodyScroll } from '@/hooks/utils/useDisableBodyScroll'
import { LottieAnimation } from '../Lottie'

import classNames from 'classnames'
import classes from './styles.module.scss'

export type BackgroundIntensity = 'transparent' | 'light' | 'medium' | 'solid'

interface LoadingOverlayProps {
  isLoading?: boolean
  lottie?: boolean
  customLottie?: any
  backgroundIntensity?: BackgroundIntensity
  /**
   * Keeps the overlay visible at least this long after it appears, so fast
   * loading sequences don't flash it in and out. 0 (default) preserves the
   * exact isLoading passthrough — mutation spinners must not linger.
   */
  minVisibleMs?: number
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = (props) => {
  const {
    isLoading = true,
    lottie = true,
    backgroundIntensity = 'medium',
    minVisibleMs = 0
  } = props

  // Hold the overlay for minVisibleMs from the moment it appears: the timer
  // starts on show (not on hide), so a load that already ran longer than the
  // minimum hides with no extra delay.
  const [minHold, setMinHold] = useState(minVisibleMs > 0 && isLoading)
  const [prevLoading, setPrevLoading] = useState(isLoading)
  if (isLoading !== prevLoading) {
    setPrevLoading(isLoading)
    if (isLoading && minVisibleMs > 0) {
      setMinHold(true)
    }
  }

  useEffect(() => {
    if (!minHold) return
    const timer = setTimeout(() => setMinHold(false), minVisibleMs)
    return () => clearTimeout(timer)
  }, [minHold, minVisibleMs])

  const isVisible = isLoading || minHold

  useDisableBodyScroll({ enabled: isVisible })

  return (
    <div
      data-testid="loading-overlay"
      className={classNames({
        [classes.container]: true,
        [classes.hide]: !isVisible,
        [classes.transparent]: backgroundIntensity === 'transparent',
        [classes.light]: backgroundIntensity === 'light',
        [classes.medium]: backgroundIntensity === 'medium',
        [classes.solid]: backgroundIntensity === 'solid'
      })}
    >
      {!lottie ?
        <div className={classes.loader} data-testid="loader" /> :
        <div className={classes.lottie} data-testid="lottie">
          <LottieAnimation />
        </div>
      }
    </div>
  )
}
